import { useState, useEffect, useCallback } from 'react'
import { PushPromptService } from './push-prompt.service'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

console.log('[usePushNotifications] VAPID key loaded:', VAPID_PUBLIC_KEY ? 'yes' : 'no')

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  
  return outputArray.buffer
}

interface PushState {
  isSupported: boolean
  isSubscribed: boolean
  subscription: PushSubscription | null
  error: string | null
  badgeCount: number
  permission: NotificationPermission
}

export function usePushNotifications(): PushState & {
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
  resetBadge: () => void
  sendTest: () => Promise<void>
  requestPermission: () => Promise<void> // Новый метод
} {
  const [state, setState] = useState<PushState>({
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    isSubscribed: false,
    subscription: null,
    error: null,
    badgeCount: 0,
    permission: 'default'
  })

  console.log('[usePushNotifications] Initial state:', state)

  // Проверка разрешения при монтировании
  useEffect(() => {
    if (!state.isSupported) {
      console.log('[usePushNotifications] Not supported')
      return
    }    
    setState(prev => ({ ...prev, permission: Notification.permission }))
    
    const check = async (): Promise<void> => {
      try {
        console.log('[usePushNotifications] Checking existing subscription...')
        const registration = await navigator.serviceWorker.ready
        console.log('[usePushNotifications] SW ready:', registration.scope)
        
        const existing = await registration.pushManager.getSubscription()
        console.log('[usePushNotifications] Existing subscription:', existing ? 'yes' : 'no')

        if (existing) {
          console.log('[usePushNotifications] Endpoint:', existing.endpoint.substring(0, 50))
        }
        
        setState(prev => ({
          ...prev,
          isSubscribed: !!existing,
          subscription: existing
        }))
      } catch (err) {
        console.error('[usePushNotifications] Check failed:', err)
        setState(prev => ({ ...prev, error: 'Check failed' }))
      }
    }

    check()
  }, [state.isSupported])

  // Слушаем бейдж от SW
  useEffect(() => {
    const handler = (e: MessageEvent): void => {
      if (e.data?.type === 'BADGE_COUNT') {
        setState(prev => ({ ...prev, badgeCount: e.data.count }))
      }
    }
    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker.removeEventListener('message', handler)
  }, [])

  // Шаг 1: Запрос разрешения (должен быть вызван из обработчика клика)
  const requestPermission = useCallback(async (): Promise<void> => {
    try {
      const permission = await Notification.requestPermission()
      setState(prev => ({ ...prev, permission }))
      
      if (permission !== 'granted') {
        setState(prev => ({ ...prev, error: 'Permission denied' }))
      }
    } catch (err) {
      setState(prev => ({ 
        ...prev, 
        error: err instanceof Error ? err.message : 'Permission request failed' 
      }))
    }
  }, [])

  // Шаг 2: Подписка (после получения разрешения)
  const subscribe = useCallback(async (): Promise<void> => {
    console.log('[usePushNotifications] subscribe() called')

    if (!VAPID_PUBLIC_KEY) {
      console.error('[usePushNotifications] No VAPID key')
      setState(prev => ({ ...prev, error: 'VAPID key not configured' }))
      return
    }

    // Проверяем разрешение перед подпиской
    if (Notification.permission !== 'granted') {
      setState(prev => ({ 
        ...prev, 
        error: 'Сначала нажмите "Разрешить уведомления"' 
      }))
      return
    }

    try {
      const registration = await navigator.serviceWorker.ready
      console.log('[usePushNotifications] Got SW registration')
      
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      console.log('[usePushNotifications] Converted VAPID key, length:', applicationServerKey)

      console.log('[usePushNotifications] Calling pushManager.subscribe...')
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })
      console.log('[usePushNotifications] Subscribed! Endpoint:', subscription.endpoint.substring(0, 50))

      // Проверяем что это не WNS (Windows)
      if (subscription.endpoint.includes('windows.com') || subscription.endpoint.includes('wns')) {
        await subscription.unsubscribe()
        setState(prev => ({ 
          ...prev, 
          error: 'Windows Push не поддерживается. Используйте Chrome или Firefox.' 
        }))
        return
      }

      // Отправляем на сервер
      console.log('[usePushNotifications] Sending to server...')
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          deviceId: crypto.randomUUID(),
          userId: 'executive'
        })
      })

      if (!response.ok) {
        throw new Error('Server rejected subscription')
      }

      console.log('[usePushNotifications] Saved to server')

      PushPromptService.setState('granted')

      setState(prev => ({
        ...prev,
        isSubscribed: true,
        subscription,
        error: null
      }))
    } catch (err) {
      console.error('[usePushNotifications] Subscribe error:', err)
      setState(prev => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Subscribe failed'
      }))
    }
  }, [])

  // Отписка
  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!state.subscription) return

    try {
      await state.subscription.unsubscribe()
      
      await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: state.subscription.endpoint,
          userId: 'executive'
        })
      })

      setState(prev => ({
        ...prev,
        isSubscribed: false,
        subscription: null
      }))
    } catch {
      setState(prev => ({ ...prev, error: 'Unsubscribe failed' }))
    }
  }, [state.subscription])

  // Сброс бейджа
  const resetBadge = useCallback((): void => {
    navigator.serviceWorker.ready.then(reg => {
      reg.active?.postMessage({ type: 'RESET_BADGE' })
      setState(prev => ({ ...prev, badgeCount: 0 }))
    })
  }, [])

  // Тестовое уведомление
  const sendTest = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch('/api/push/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: 'executive',
          title: 'Тест Argus',
          body: 'Push-уведомления работают!'
        })
      })
      
      if (!res.ok) throw new Error('Server error')
    } catch {
      setState(prev => ({ ...prev, error: 'Test send failed' }))
    }
  }, [])

  return {
    ...state,
    subscribe,
    unsubscribe,
    resetBadge,
    sendTest,
    requestPermission
  }
}